// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import {
  HOLE_FRAME_MARGIN_METERS,
  ringAreaSquareMeters,
  shapesNearHole,
  SHAPE_DEPTH,
  type HoleShape,
} from '@/features/hole/holeview/holeShapes';
import { distanceToSegmentMeters, type LatLon } from '@/features/hole/holeview/tileMath';

import type { HoleShapeFeature } from './useHoleFeatures';

/**
 * Which mapped boundaries belong to a hole, and how to describe them as switchable layers.
 *
 * Split out of the modal because it is the part with rules in it: the hole view draws what it
 * is given, but deciding that a fairway component reaching into hole 4 belongs on hole 4's
 * picture — while the row itself is filed under hole 3 — is a judgement that deserves tests
 * rather than a paragraph of memo() in a component.
 */

/** One switchable kind, with enough of a summary to decide whether to look at it. */
export type ShapeLayer = {
  kind: string;
  count: number;
  /** Summed across the kind, so "fairway 2 · 14 200 m²" reads as one figure. */
  areaSquareMeters: number;
};

/**
 * The boundaries that make up one hole's picture.
 *
 * Two ways in, deliberately. Geometrically, anything reaching the hole's frame — which is how
 * the in-round view picks shapes, and the only way a fairway spanning two holes appears on
 * both. And by attribution, anything filed under this hole number — which catches the row
 * sitting somewhere impossible, precisely the row an admin opened this screen to find. A
 * boundary hidden because it is nowhere near the hole it claims to be on would be the one
 * mistake this screen cannot show.
 *
 * A hole with nothing marked yet has no frame to measure by, so neither way finds anything —
 * which would hide the very shapes an admin needs to see before clicking "Set green". There
 * every mapped shape is offered instead, filtered by the same kind switches, so the analyser's
 * result is what tells you where to place the marker rather than the marker being needed
 * first. Placing one narrows the picture back down on its own next render.
 */
export const shapesForHole = (
  shapes: HoleShapeFeature[],
  holeNumber: number | null,
  tees: LatLon[],
  green: LatLon | null
): HoleShapeFeature[] => {
  const near = new Set(shapesNearHole(shapes, tees, green).map((shape) => shape.id));

  const matched = shapes.filter(
    (shape) =>
      shape.ring.length >= 3 &&
      (near.has(shape.id) || (holeNumber !== null && shape.holeNumber === holeNumber))
  );

  if (matched.length === 0 && tees.length === 0 && !green) {
    return shapes.filter((shape) => shape.ring.length >= 3).sort(byDepthThenId);
  }

  return matched.sort(byDepthThenId);
};

/**
 * What the map's frame is fit to, as opposed to what it draws.
 *
 * {@link shapesForHole} deliberately keeps two things the frame has no business following.
 * A row filed under this hole while sitting hundreds of metres away — the misattribution
 * this screen exists to expose — and the far end of a cover polygon that merely reaches the
 * hole's frame, since an OSM fairway spanning three holes is one ring and is kept whole.
 * Fitting on either pulls the view out to the whole course: a confirmed 400 m hole ends up
 * drawn at 40% of the canvas, with the stored basemap running out before the canvas edge.
 *
 * So the frame is fit to the hole's own corridor — the vertices within `marginMeters` of a
 * tee-to-green line, the same margin {@link shapesNearHole} selects shapes by. The far
 * geometry is still drawn and still listed underneath; it just no longer decides the zoom,
 * and panning out reaches it.
 *
 * An unmapped hole has no corridor to measure against, and keeps the wide every-shape frame
 * that doubles as a course overview (see {@link shapesForHole}'s own doc).
 *
 * Rings, not shapes: the result is only ever fed to the map's `frame`, which flattens it to
 * vertices. Returning clipped {@link HoleShapeFeature}s would be a lie about geometry that
 * nothing draws.
 */
export const framingRingsForHole = (
  shapes: HoleShapeFeature[],
  holeNumber: number | null,
  tees: LatLon[],
  green: LatLon | null,
  marginMeters: number = HOLE_FRAME_MARGIN_METERS
): { ring: LatLon[] }[] => {
  const forHole = shapesForHole(shapes, holeNumber, tees, green);
  const corridors: Array<[LatLon, LatLon]> = green
    ? tees.filter(isFinitePoint).map((tee) => [tee, green])
    : [];
  if (corridors.length === 0) return forHole.map((shape) => ({ ring: shape.ring }));

  return forHole
    .map((shape) => ({
      ring: shape.ring.filter(
        (vertex) =>
          isFinitePoint(vertex) &&
          corridors.some(([from, to]) => distanceToSegmentMeters(vertex, from, to) <= marginMeters)
      ),
    }))
    .filter((framed) => framed.ring.length > 0);
};

/**
 * The same frame `factor` times further out, around the same centre.
 *
 * Every point moves away from the middle of their bounding box, and the map fits a box around
 * the points with margins in proportion to it — so the fit lands where `frame`'s would, showing
 * `factor` times the ground. Fit-only, like everything fed to the map's `frame`: the points it
 * returns are where nothing is.
 */
export const zoomedOutFrame = <Tee extends LatLon>(
  frame: { tees: Tee[]; green: LatLon | null; polygons: { ring: LatLon[] }[] },
  factor: number
): { tees: Tee[]; green: LatLon | null; polygons: { ring: LatLon[] }[] } => {
  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLon = Infinity;
  let maxLon = -Infinity;
  const points = [
    ...frame.tees,
    ...(frame.green ? [frame.green] : []),
    ...frame.polygons.flatMap((polygon) => polygon.ring),
  ];
  for (const point of points) {
    if (!isFinitePoint(point)) continue;
    minLat = Math.min(minLat, point.lat);
    maxLat = Math.max(maxLat, point.lat);
    minLon = Math.min(minLon, point.lon);
    maxLon = Math.max(maxLon, point.lon);
  }
  if (minLat > maxLat) return frame;

  const center = { lat: (minLat + maxLat) / 2, lon: (minLon + maxLon) / 2 };
  const away = <Point extends LatLon>(point: Point): Point => ({
    ...point,
    lat: center.lat + (point.lat - center.lat) * factor,
    lon: center.lon + (point.lon - center.lon) * factor,
  });
  return {
    tees: frame.tees.map(away),
    green: frame.green ? away(frame.green) : null,
    polygons: frame.polygons.map((polygon) => ({ ring: polygon.ring.map(away) })),
  };
};

/** Local copy: holeShapes.ts keeps its own private one rather than exporting it. */
const isFinitePoint = (point: LatLon | null | undefined): point is LatLon =>
  !!point && Number.isFinite(point.lat) && Number.isFinite(point.lon);

/** Cover under hazards under the green, and a stable tiebreak so rows do not jump about. */
const byDepthThenId = (a: HoleShape, b: HoleShape): number => {
  const depth = (SHAPE_DEPTH[a.kind] ?? 9) - (SHAPE_DEPTH[b.kind] ?? 9);
  if (depth !== 0) return depth;
  return a.id.localeCompare(b.id);
};

/**
 * The kinds present, in draw order, each with its tally.
 *
 * Derived from the shapes actually on the hole rather than from a fixed list: offering a
 * "Trees" switch on a hole with no trees mapped says the analyser found some when it did not.
 */
export const layersOf = (shapes: HoleShape[]): ShapeLayer[] => {
  const byKind = new Map<string, ShapeLayer>();

  for (const shape of shapes) {
    const layer = byKind.get(shape.kind) ?? { kind: shape.kind, count: 0, areaSquareMeters: 0 };
    layer.count += 1;
    layer.areaSquareMeters += ringAreaSquareMeters(shape.ring);
    byKind.set(shape.kind, layer);
  }

  return [...byKind.values()].sort(
    (a, b) => (SHAPE_DEPTH[a.kind] ?? 9) - (SHAPE_DEPTH[b.kind] ?? 9)
  );
};

/**
 * The shapes left once the switched-off kinds are dropped.
 *
 * Hidden kinds are tracked rather than shown ones so that a kind appearing for the first time
 * — a new hole, or a re-analysis that finally found water — is visible by default. Tracking
 * the shown set would leave every newly detected kind switched off and invisible, which looks
 * exactly like the analyser having failed.
 */
export const visibleShapes = <T extends HoleShape>(shapes: T[], hiddenKinds: string[]): T[] =>
  hiddenKinds.length === 0 ? shapes : shapes.filter((shape) => !hiddenKinds.includes(shape.kind));

/** Switches one kind, leaving the rest alone. */
export const toggleKind = (hiddenKinds: string[], kind: string): string[] =>
  hiddenKinds.includes(kind)
    ? hiddenKinds.filter((hidden) => hidden !== kind)
    : [...hiddenKinds, kind];
